// Ошибка загрузки чанка — признак того, что сервер обновили: вкладка просит файлы старой сборки, которых больше нет.
// Лечится тихой перезагрузкой страницы. Решение принимает `createChunkReloadGuard`, показ страницы ошибки —
// `AppErrorBoundary` (`@srm/ui/shell/AppErrorBoundary`) получает экземпляр пропсом.

// Текст отказа динамического импорта у каждого движка свой: Chromium — «Failed to fetch dynamically imported module»,
// Firefox — «error loading dynamically imported module», Safari — «Importing a module script failed.».
const CHUNK_ERROR_MARKERS = [
    'Loading chunk',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    'Importing a module script failed',
    'Loading CSS chunk',
    'Unable to preload CSS',
    'ChunkLoadError',
];

const messageOf = (error: unknown): string => {
    if (typeof error === 'string') return error;
    const message = (error as { message?: unknown } | null | undefined)?.message;
    if (typeof message === 'string') return message;
    return typeof error === 'number' || typeof error === 'boolean' ? String(error) : '';
};

/** Ошибка загрузки чанка (динамического импорта, CSS чанка): строка, Error или событие с таким текстом. */
export const isChunkError = (error: unknown): boolean => {
    const message = messageOf(error);
    return CHUNK_ERROR_MARKERS.some((marker) => message.includes(marker));
};

/**
 * Окно после тихой перезагрузки. Повторная ошибка чанка в нём — уже не устаревшая сборка (после перезагрузки она
 * свежая), а сбой сервера или сети: показывается страница ошибки, петли перезагрузок нет. Загрузка, проработавшая окно,
 * — успешная: следующая ошибка чанка (следующее обновление стенда) снова лечится перезагрузкой.
 */
export const CHUNK_RELOAD_WINDOW_MS = 60 * 1000;

/** Ключ sessionStorage: Front и ControlPanel живут на одном origin и делят вкладку, ключ — у каждого свой. */
export const chunkReloadStorageKey = (namespace: string): string => `${namespace}:chunk-reload-at`;

export interface IChunkReloadGuardOptions {
    /** Пространство приложения в ключе (`front`, `controlpanel`). */
    namespace: string;
    /** @default () => sessionStorage */
    storage?: () => Storage;
    /** @default () => window.location.reload() */
    reload?: () => void;
    /** @default Date.now */
    now?: () => number;
}

export interface IChunkReloadGuard {
    readonly storageKey: string;
    /** Ошибка загрузки чанка и перезагрузка поможет. Без эффектов: годится для решения при отрисовке. */
    readonly shouldReload: (error: unknown) => boolean;
    /** Ошибка загрузки чанка: перезагрузить страницу, если поможет. true — перезагрузка пошла. */
    readonly tryReload: (error: unknown) => boolean;
}

export function createChunkReloadGuard({
    namespace,
    storage = () => sessionStorage,
    reload = () => window.location.reload(),
    now = () => Date.now(),
}: IChunkReloadGuardOptions): IChunkReloadGuard {
    const storageKey = chunkReloadStorageKey(namespace);

    /** Время прошлой тихой перезагрузки; `undefined` — хранилище недоступно (запомнить нельзя — не перезагружаем). */
    const reloadedAt = (): number | null | undefined => {
        try {
            const raw = storage().getItem(storageKey);
            const at = raw === null ? NaN : Number(raw);
            return Number.isFinite(at) ? at : null;
        } catch {
            return undefined;
        }
    };

    const shouldReload = (error: unknown): boolean => {
        if (!isChunkError(error)) return false;
        const at = reloadedAt();
        if (at === undefined) return false;
        if (at === null) return true;
        const sinceReload = now() - at;
        return sinceReload >= CHUNK_RELOAD_WINDOW_MS || sinceReload < 0;
    };

    return {
        storageKey,
        shouldReload,
        tryReload(error) {
            if (!isChunkError(error)) return false;
            if (!shouldReload(error)) {
                console.warn('Chunk error occurred again after reload. Showing ErrorPage...');
                return false;
            }
            try {
                storage().setItem(storageKey, String(now()));
            } catch {
                return false;
            }
            console.warn('Chunk error detected. Reloading page once...');
            reload();
            return true;
        },
    };
}
